import { eachDayOfInterval, format, getISODay, parseISO } from 'date-fns'
import { uk } from 'date-fns/locale'

import { IndividualPlanDetailDTO, IndividualPlanSubjectEventDTO, IndividualPlanTemplateDetailDTO } from './types/globals'

export const nameFromUrl = (url: string): string => {
  const splitUrl = url.split('/')
  return splitUrl[splitUrl.length - 1]
}

export function uploadRulesCheck(callback: (file: File) => void, files: File[], types: string[], setError: (err: string) => void): void {
  for (const file of files) {
    const fileExtension = file.name.split('.').splice(-1)[0]
    if (!types.map((i) => i.toLowerCase()).includes(fileExtension.toLowerCase())) {
      setError(`Розширення файлу '${fileExtension}' не дозволено. Дозволені розширення: ${types.join(', ')}.`)
      break
    }

    const fileSize = file.size / 1024 / 1024
    if (fileSize > 20) {
      setError(`Максимальный размер файла 20Mb`)
      break
    }

    callback(file)
  }
}

export const fillWeeks = (start: string, end: string) => {
  const startDate = parseISO(start)
  const endDate = parseISO(end)

  const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate })

  // Calculate padding for start/end of week
  const startPadding = getISODay(startDate) - 1
  const endPadding = 7 - getISODay(endDate)

  // Create padding arrays
  const startArray = Array(startPadding).fill(null)
  const endArray = Array(endPadding).fill(null)

  // Concatenate all arrays to obtain one array representing the month with padding
  const monthArray = [...startArray, ...daysInMonth, ...endArray]

  const formattedMonthArray = monthArray.map((day) => (!!day ? format(day, 'yyyy-MM-dd') : day))

  // Split array into weeks
  return Array.from({ length: Math.ceil(formattedMonthArray.length / 7) }, (_, i) => formattedMonthArray.slice(i * 7, i * 7 + 7))
}

export function formatDateInGenitive(date: Date): string {
  const genitiveMonths = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня']

  const formattedTime = format(date, 'dd LLLL yyyy', { locale: uk })

  const monthName = format(date, 'LLLL', { locale: uk })
  const monthNameGenitive = genitiveMonths[date.getMonth()]

  return formattedTime.replace(monthName, monthNameGenitive)
}

export function addEventsToIndividualPlanSubjects<T extends IndividualPlanDetailDTO | IndividualPlanTemplateDetailDTO>(subjects?: T['subjects'], events?: T['events']) {
  if (!subjects || !events) return []

  return subjects.map((subj) => {
    // Для каждого месяца фильтруем события по предмету
    const eventsByMonth = events.map((eventList) => {
      const filteredEvents = ((eventList?.events as { subject_id: string }[])?.filter((event) => event.subject_id === subj.subject.id) || []) as T['events'][number]['events']
      return {
        ...eventList,
        events: filteredEvents,
      }
    })

    // Находим максимальное количество событий в месяцах для этого предмета
    const maxLength = Math.max(1, ...eventsByMonth.map((ev) => ev.events.length))

    // Дополняем массивы событий до максимальной длины
    const normalizedEventsByMonth = eventsByMonth.map((ev) => ({
      ...ev,
      events:
        ev.events.length < maxLength
          ? ([...ev.events, ...Array.from({ length: maxLength - ev.events.length }).fill(undefined)] as Array<T['events'][number]['events'][number] | undefined>)
          : ev.events,
    }))

    return {
      ...(subj as T['subjects'][number]),
      subjId: subj.id,
      events: normalizedEventsByMonth as T['events'],
    }
  })
}

export function fillDatesBetween(startStr: string, endStr: string): Date[] {
  const start = new Date(startStr)
  const end = new Date(endStr)
  const dates: Date[] = []
  let current = start
  while (current <= end) {
    dates.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }
  return dates
}

/**
 * Sorts classroom titles in numeric order (e.g., "1 клас", "2 клас", ..., "10 клас")
 * @param a - First classroom title
 * @param b - Second classroom title
 * @returns Sort comparison result
 */
export function sortClassrooms(a: string, b: string): number {
  return a.localeCompare(b, 'uk', { numeric: true, sensitivity: 'base' })
}
